import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  signal,
  input,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { TreeModule } from 'primeng/tree';
import { Divider } from 'primeng/divider';
import { TreeNode } from 'primeng/api';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideLogOut,
  lucidePhone,
  lucideHouse,
  lucideUsers,
  lucideUser,
  lucideChartLine,
  lucideSwatchBook,
  lucideBuilding2,
  lucideLightbulb,
  lucideAlbum,
  lucideLaptop2,
  lucideListChecks,
  lucideHelpCircle,
} from '@ng-icons/lucide';
import { environment } from '../../../../environments/environment';
import { BadgeModule } from 'primeng/badge';
import { RouterModule } from '@angular/router';
import { PopoverModule } from 'primeng/popover';
import { NavBarService } from '../nav-bar.service';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../../core/services/auth.service';
import { navbarItems } from '../../header/navbar-items';
import { PersianTitlesService } from '../../../core/services/persian-titles.service';
import { NavigationService } from '../../../shared/services/navigation.service';
import { TourService } from '../../../core/services/tour.service';
import { TourStepId } from '../../../core/models/enums/tour-step-id.enum';
import { TourStepDirective } from 'src/app/shared/directives/tour-step.directive';

@Component({
  selector: 'app-navigation-drawer',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TreeModule,
    Divider,
    NgIcon,
    BadgeModule,
    PopoverModule,
    DrawerModule,
    ButtonModule,
    TourStepDirective,
  ],
  templateUrl: './navigation-drawer.component.html',
  styleUrls: ['./navigation-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    provideIcons({
      lucideLogOut,
      lucidePhone,
      lucideHouse,
      lucideUsers,
      lucideUser,
      lucideChartLine,
      lucideSwatchBook,
      lucideBuilding2,
      lucideLightbulb,
      lucideAlbum,
      lucideLaptop2,
      lucideListChecks,
      lucideHelpCircle,
    }),
  ],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-8px)' }),
        animate(
          '150ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '120ms ease-in',
          style({ opacity: 0, transform: 'translateY(-8px)' }),
        ),
      ]),
    ]),
    trigger('fadeFast', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.96)' }),
        animate('120ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [
        animate(
          '90ms ease-in',
          style({ opacity: 0, transform: 'scale(0.98)' }),
        ),
      ]),
    ]),
    trigger('fadeBackdrop', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('150ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('120ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
})
export class NavigationDrawerComponent {
  readonly modalOpen = input<boolean>(false);

  readonly navBarService = inject(NavBarService);
  readonly navigationService = inject(NavigationService);
  readonly tourService = inject(TourService);

  private readonly authService = inject(AuthService);
  private readonly navBarItems = navbarItems(this.persianTitlesService);

  readonly TourStepId = TourStepId;

  readonly isOpen = this.navBarService.isNavDrawerOpen;
  readonly isOpenMobile = this.navBarService.isNavDrawerOpenMobile;
  readonly baseApi = environment.baseApi;

  readonly callSupportExpanded = signal(false);
  readonly avatarUrl = signal<string>('');
  readonly items = signal<TreeNode[]>(this.buildMenuItems());

  constructor(private persianTitlesService: PersianTitlesService) {
    if (this.tourService.isMobile) {
      const openMobileDrawer = () =>
        this.navBarService.isNavDrawerOpenMobile.set(true);

      const closeMobileDrawer = () =>
        this.navBarService.isNavDrawerOpenMobile.set(false);

      this.tourService.registerStepCallbacks(TourStepId.Sidebar, {
        beforeShow: openMobileDrawer,
        afterHide: closeMobileDrawer,
      });

      this.tourService.registerStepCallbacks(TourStepId.PortalGuide, {
        beforeShow: openMobileDrawer,
        afterHide: closeMobileDrawer,
      });

      this.tourService.registerStepCallbacks(TourStepId.SupportLogout, {
        beforeShow: openMobileDrawer,
        afterHide: closeMobileDrawer,
      });
    }
  }

  toggle(): void {
    this.isOpen.update((v) => !v);
    this.items.update((v) => v.map((item) => ({ ...item, expanded: false })));
  }

  closeMenusAfterNavigation(): void {
    if (this.modalOpen()) return;

    if (this.isOpenMobile()) {
      this.isOpenMobile.set(false);
    } else if (this.isOpen()) {
      this.isOpen.set(false);
    }

    this.items.update((v) => v.map((item) => ({ ...item, expanded: false })));
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) this.isOpen.set(false);
  }

  private buildMenuItems(): TreeNode[] {
    return this.navBarItems.map((e) => {
      return {
        id: e.id,
        label: e.name,
        icon: e.icon,
        data: {
          link: e.urlParts,
        },
        children: e.items?.map((se) => {
          return {
            id: se.id,
            label: se.name,
            data: {
              link: se.urlParts,
            },
          };
        }),
      };
    });
  }

  startTour(): void {
    // Ensure the desktop drawer is open and mobile drawer is closed before
    // the tour initialises, so step elements in the sidebar are in the DOM.
    this.navBarService.isNavDrawerOpen.set(true);
    this.navBarService.isNavDrawerOpenMobile.set(false);

    this.tourService.startTour();
  }

  logout(): void {
    const fullscreenPlayer = document.querySelector(
      'media-player[data-fullscreen]',
    );
    if (fullscreenPlayer) {
      // ignore accidental click while exiting fullscreen
      return;
    }
    this.authService.logout();
  }

  isArray(value: unknown): boolean {
    return Array.isArray(value);
  }

  handleItemClick(item: TreeNode): void {
    if (item.data?.link) {
      this.navigationService.clear();
      this.isOpenMobile.set(false);
    } else {
      item.expanded = !item.expanded;
    }
  }

  handleSubItemClick(): void {
    this.navigationService.clear();
    this.collapseAllItems();
    this.isOpenMobile.set(false);
  }

  collapseAllItems(): void {
    this.items.update((v) => v.map((item) => ({ ...item, expanded: false })));
    this.navBarService.isNavDrawerOpenMobile.set(false);
  }
}
